import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
} from "@mui/material";
import {
  Event as EventIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useUnifiedTranslation } from "../../hooks/useUnifiedTranslation";
import { eventsApi } from "../../services/api/events.api";
import { formatShortDate } from "../../utils/date-utils";
import { PAGE_SIZE } from "../../constants/pagination";

export const GuestDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: "dashboard" });
  const ts = (key: string, fallback: string): string => {
    const v = t(key);
    return typeof v === "string" ? v : fallback;
  };
  const navigate = useNavigate();

  // Загрузка публичных событий
  const { data: upcomingEvents, isLoading: isLoadingEvents } = useQuery({
    queryKey: ["guest", "upcoming-events"],
    queryFn: async () => {
      const response = await eventsApi.getEvents({
        isPublic: true,
        limit: PAGE_SIZE.SMALL,
      });
      // ADR-002: envelope.data — массив событий
      return response.data || [];
    },
  });

  // Загрузка популярных событий
  const { data: popularEvents, isLoading: isLoadingPopular } = useQuery({
    queryKey: ["guest", "popular-events"],
    queryFn: async () => {
      const response = await eventsApi.getEvents({
        isPublic: true,
        limit: PAGE_SIZE.MICRO,
      });
      // ADR-002: envelope.data — массив событий
      return response.data || [];
    },
  });

  // Загрузка рекомендованных событий (используем фильтр isPublic)
  const { data: featuredEvents, isLoading: isLoadingFeatured } = useQuery({
    queryKey: ["guest", "featured-events"],
    queryFn: async () => {
      const response = await eventsApi.getEvents({
        isPublic: true,
        limit: PAGE_SIZE.MICRO,
      });
      // ADR-002: envelope.data — массив событий
      return response.data || [];
    },
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Приветствие и призыв к действию */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Typography variant="h3" gutterBottom>
          {ts("guest.welcome", "Добро пожаловать!")}
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
          {ts("guest.subtitle", "Откройте для себя лучшие мероприятия")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RegisterIcon />}
            onClick={() => navigate("/register")}
            sx={{
              bgcolor: "white",
              color: "primary.main",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Зарегистрироваться
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<LoginIcon />}
            onClick={() => navigate("/login")}
            sx={{
              borderColor: "white",
              color: "white",
              "&:hover": {
                borderColor: "grey.100",
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Войти
          </Button>
        </Box>
      </Paper>

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Доступных событий
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingEvents ? (
                      <CircularProgress size={24} />
                    ) : (
                      upcomingEvents?.length || 0
                    )}
                  </Typography>
                </Box>
                <EventIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Популярных
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingPopular ? (
                      <CircularProgress size={24} />
                    ) : (
                      popularEvents?.length || 0
                    )}
                  </Typography>
                </Box>
                <TrendingIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Рекомендуем
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingFeatured ? (
                      <CircularProgress size={24} />
                    ) : (
                      featuredEvents?.length || 0
                    )}
                  </Typography>
                </Box>
                <StarIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Предстоящие события */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Предстоящие события
          </Typography>
          {isLoadingEvents ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            <List>
              {upcomingEvents.slice(0, 5).map((event: any) => (
                <ListItem
                  key={event.id}
                  secondaryAction={
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      Подробнее
                    </Button>
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      <CalendarIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={event.title}
                    secondary={
                      <>
                        {formatShortDate(event.startDate)}
                        {" • "}
                        {event.location?.city || "Онлайн"}
                        {event.category && (
                          <>
                            {" • "}
                            <Chip
                              label={event.category}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">Нет предстоящих событий</Alert>
          )}
        </CardContent>
      </Card>

      {/* Популярные события */}
      {popularEvents && popularEvents.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Популярные события
            </Typography>
            <Grid container spacing={2}>
              {popularEvents.map((event: any) => (
                <Grid item xs={12} md={6} key={event.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {event.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {event.description?.substring(0, 100)}...
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        <Typography variant="caption">
                          {formatShortDate(event.startDate)}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => navigate(`/events/${event.id}`)}
                        >
                          Подробнее
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GuestDashboard;
